import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import type { ControllerFlowAPI, Experiments } from '@wix/yoshi-flow-editor';
import settingsParams from '../../../../components/BookingsForm/settingsParams';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import type { ServicePayment } from '../../../mappers/serviceMapper/utils/payment.mapper';
import type { CoveringPurchasableBenefit } from '../../benefitCoverage/benefitCoverage';
import {
  buildBuyPlanAndBookOptionId,
  createBuyPlanFromPurchasablePlanOption,
  DepositOrFullAmountOptionIds,
  getPurchasablePlanVariantId,
  getUniquePurchasablePlans,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTimeOnly,
  isOfferedAsPricingPlanOnly,
  isServiceHavePricingPlans,
} from '../utils';
import { isPlanAndBookEnabled } from '../../plans/utils';

export const getDefaultPaymentOptionId = ({
  settings,
  servicePayment,
  memberships,
  isPricingPlanInstalled,
  isDynamicPricingCustomOptions,
  hasEligiblePlans,
  experiments,
  paymentTypes,
  purchasableBenefits,
  excludePricingPlanOptions = false,
  isPlanAndBookBlocked,
  isPricingPlansMigratedToEcom,
}: {
  settings: ControllerFlowAPI['settings'];
  servicePayment: ServicePayment;
  isPricingPlanInstalled: boolean;
  memberships?: ListEligibleMembershipsResponse;
  isDynamicPricingCustomOptions?: boolean;
  hasEligiblePlans?: boolean;
  experiments?: Experiments;
  paymentTypes?: PaymentMethod[];
  purchasableBenefits?: CoveringPurchasableBenefit[];
  excludePricingPlanOptions?: boolean;
  /** ECP-12919, booking-level — see bookingRequiresStoredPaymentMethod. */
  isPlanAndBookBlocked?: boolean;
  /** Whether the site's Pricing Plans are migrated to eCom — see isPlanAndBookEnabled. */
  isPricingPlansMigratedToEcom?: boolean;
}) => {
  // Plan & Book: where the legacy flow defaults to the generic "Buy a plan" entry,
  // default to the first concrete purchasable plan option instead.
  // ECP-12919 (see bookingRequiresStoredPaymentMethod): when Plan & Book is hidden for the
  // booking, default to the first plan's plain "Buy a plan" id instead.
  const buyPlanDefaultOptionId = () => {
    if (
      isPlanAndBookEnabled({ experiments, isPricingPlansMigratedToEcom }) &&
      purchasableBenefits?.length
    ) {
      if (isPlanAndBookBlocked) {
        const firstPlan = getUniquePurchasablePlans(
          purchasableBenefits,
          experiments,
        )[0];
        if (firstPlan) {
          // Reuse the same builder as the rendered option so the id always matches —
          // otherwise a multi-variant plan's default id (bare planId) wouldn't match
          // any option's real id (planId:pricingVariantId).
          return createBuyPlanFromPurchasablePlanOption(firstPlan, experiments)
            .id;
        }
      } else {
        const { plan, benefit } = purchasableBenefits[0];
        return buildBuyPlanAndBookOptionId(
          plan.catalogReference?.catalogItemId,
          benefit.benefitId,
          getPurchasablePlanVariantId(plan.catalogReference, experiments),
        );
      }
    }
    return ReservedPaymentOptionIds.BuyAPricingPlan;
  };

  const getDefaultSingleSessionPaymentOptionId = () =>
    isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        servicePayment.paymentDetails.fullUpfrontPaymentAllowed,
      deposit: servicePayment.paymentDetails.minCharge,
      paymentTypes: paymentTypes ?? [],
    })
      ? DepositOrFullAmountOptionIds.Deposit
      : ReservedPaymentOptionIds.SingleSession;

  if (
    servicePayment.paymentDetails.subscriptionInfo &&
    experiments?.enabled(ExperimentsConsts.SubscriptionPricingUoU)
  ) {
    return ReservedPaymentOptionIds.Subscription;
  }

  // SCHED-48175: a variant (custom-options) service must not default to a plan option the
  // buyer would still have to purchase — but a plan they *already own* should win, exactly as
  // it does for fixed-price services. Before this FT the return below came first, so the
  // eligible-membership lookup was never reached for variant services: the form showed
  // "Pay now" while selectedPaymentType stayed MEMBERSHIP, and the booking was then created
  // as plan-paid, priced at 0, and skipped checkout without redeeming the plan.
  const planWinsForVariantServices = experiments?.enabled(
    ExperimentsConsts.PlanDefaultForVariantServicesUou,
  );

  if (isDynamicPricingCustomOptions && !planWinsForVariantServices) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  const planId = excludePricingPlanOptions
    ? undefined
    : memberships?.eligibleMemberships?.[0]?.id; // todo: change to Selected Plan when implemented

  if (planId) {
    return planId;
  }

  // No owned plan: a variant service still falls back to single-session, so the form never
  // pre-selects "Buy a plan" for it (SCHED-28521). This reproduces the pre-FT behaviour exactly
  // — the early return above did the same for every variant service, owned plan or not.
  //
  // Known pre-existing gap, deliberately not widened here: when the service is offered *only*
  // as a pricing plan, getPaymentOptions emits no single-session entry at all
  // (showPaySingleSession = isOfferedAsOneTime), so this id matches nothing and
  // getFirstAvailablePaymentOption returns its NoAvailablePaymentOptions sentinel. Note it only
  // falls back to another option when the selected one exists *but is disabled*, so the valid
  // "Buy a plan" entry sitting in the list does not rescue this. Characterised for both FT
  // states in getFirstAvailablePaymentOption.spec.ts; fixing it is a separate change.
  if (isDynamicPricingCustomOptions) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  if (
    excludePricingPlanOptions ||
    isOfferedAsOneTimeOnly(servicePayment) ||
    !isPricingPlanInstalled
  ) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  if (isOfferedAsPricingPlanOnly(servicePayment)) {
    return buyPlanDefaultOptionId();
  }

  const selected =
    [
      PaymentMethod.SINGLE,
      PaymentMethod.ONLINE,
      PaymentMethod.OFFLINE,
    ].includes(settings.get(settingsParams.defaultPaymentMethod)) ||
    !isServiceHavePricingPlans(servicePayment) ||
    !hasEligiblePlans
      ? getDefaultSingleSessionPaymentOptionId()
      : buyPlanDefaultOptionId();
  return selected;
};
